import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status/enum string the M37 Judiciary and M38 Attorney
// General & Legislation APIs actually return (see src/lib/types.ts).
// Unmapped statuses still render, just in the neutral slate tone.
const STATUS_TONES: Record<string, string> = {
  // ── M37 CourtCase.status ──────────────────────────────────────────────
  FILED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SERVED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ISSUE_JOINED: 'bg-violet-50 text-violet-700 ring-violet-200',
  HEARING_SCHEDULED: 'bg-amber-50 text-amber-700 ring-amber-200',
  JUDGMENT_RESERVED: 'bg-amber-50 text-amber-700 ring-amber-200',
  JUDGMENT_DELIVERED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  DISCONTINUED: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M37 Filing.status ─────────────────────────────────────────────────
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  FEE_PENDING: 'bg-amber-50 text-amber-700 ring-amber-200',
  ACCEPTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M37 HearingSchedule.status ────────────────────────────────────────
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-200',
  HELD: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  ADJOURNED: 'bg-amber-50 text-amber-700 ring-amber-200',
  VACATED: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M37 Judgment.outcome / Dispatch.status ────────────────────────────
  GRANTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DISMISSED: 'bg-rose-50 text-rose-700 ring-rose-200',
  CONVICTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  ACQUITTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  SETTLED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DEFAULT_JUDGMENT: 'bg-amber-50 text-amber-700 ring-amber-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  DISPATCHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  PENDING_DISPATCH: 'bg-amber-50 text-amber-700 ring-amber-200',
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  PUBLISHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // ── M38 Legislation.status / LegislationVersion.status ────────────────
  IN_FORCE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  REPEALED: 'bg-rose-50 text-rose-700 ring-rose-200',
  SUPERSEDED: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M38 GazetteIssue.status ────────────────────────────────────────────
  COMPILING: 'bg-amber-50 text-amber-700 ring-amber-200',
  // PUBLISHED already mapped above.

  // ── M38 LegalNoticeSubmission.status ───────────────────────────────────
  // SUBMITTED/FEE_PENDING/REJECTED already mapped above.
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // PUBLISHED already mapped above.

  // ── M38 DraftingProject.status ────────────────────────────────────────
  INSTRUCTIONS_RECEIVED: 'bg-sky-50 text-sky-700 ring-sky-200',
  DRAFTING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  CONSULTATION: 'bg-violet-50 text-violet-700 ring-violet-200',
  CABINET_CLEARANCE: 'bg-amber-50 text-amber-700 ring-amber-200',
  ASSEMBLY_HANDOFF: 'bg-amber-50 text-amber-700 ring-amber-200',
  ENACTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M38 BillDraft.status ───────────────────────────────────────────────
  CIRCULATED: 'bg-sky-50 text-sky-700 ring-sky-200',
  // SUPERSEDED already mapped above.
  FINAL: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // ── M38 LegalOpinionRequest.status ────────────────────────────────────
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  IN_REVIEW: 'bg-amber-50 text-amber-700 ring-amber-200',
  OPINION_ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // WITHDRAWN already mapped above.

  // ── M38 LitigationCaseRef.status ──────────────────────────────────────
  OPEN: 'bg-sky-50 text-sky-700 ring-sky-200',
  WON: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  LOST: 'bg-rose-50 text-rose-700 ring-rose-200',
  // SETTLED/DISCONTINUED already mapped above.

  // ── M38 ConsultationComment.resolved (client-derived pseudo-status) ────
  RESOLVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // OPEN already mapped above.
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

/** Small pill-tab bar — same visual shape every page of this portal uses to switch panels. */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: ReadonlyArray<{ key: K; label: string }>;
  value: K;
  onChange: (key: K) => void;
}) {
  return (
    <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            value === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Inline warning banner for "your persona lacks role X" — same message pattern used throughout servicedesk-portal. */
export function RoleNotice({ roles }: { roles: string[] }) {
  return (
    <div className="mb-4">
      <ErrorNote message={`Requires ${roles.join(' or ')} — the actions below will be rejected by the API (403). You can still view this page.`} />
    </div>
  );
}
